import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { rollupFor, RollupBucket } from "../lib/api";
import { format } from "../lib/format";

interface Props {
  scopeType: "godown" | "district" | "state" | "enterprise";
  scopeId: number;
  title: string;
  subtitle?: string;
}

const PIE_COLORS = ["#2e7d32", "#c62828", "#f9a825", "#607d8b"];

export function RollupDashboard({ scopeType, scopeId, title, subtitle }: Props) {
  const [granularity, setGranularity] =
    useState<"day" | "week" | "month">("day");

  const q = useQuery({
    queryKey: ["rollup", scopeType, scopeId, granularity],
    queryFn: () => rollupFor(scopeType, scopeId, { granularity }),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">
            {scopeType}
          </div>
          <h1 className="text-2xl font-semibold text-agrilens-green-dark">
            {title}
          </h1>
          {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Bucket:</span>
          {(["day", "week", "month"] as const).map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1 rounded ${
                granularity === g
                  ? "bg-agrilens-green text-white"
                  : "bg-white border hover:bg-gray-50"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </header>

      {q.isLoading && <div className="text-gray-600">Loading rollup…</div>}
      {q.error && (
        <div className="bg-red-50 text-red-700 p-3 rounded">
          {q.error.message}
        </div>
      )}

      {q.data && <RollupBody buckets={q.data.buckets} />}

      {q.data && (
        <div className="text-xs text-gray-400">
          window {q.data.date_from} → {q.data.date_to} · computed in{" "}
          {q.data.computed_in_ms} ms
        </div>
      )}
    </div>
  );
}

function RollupBody({ buckets }: { buckets: RollupBucket[] }) {
  const totals = buckets.reduce(
    (acc, b) => {
      acc.rst += b.rst_count;
      acc.net += b.net_weight_kg_total;
      acc.samples += b.sample_count;
      acc.pass += b.pass_count;
      acc.fail += b.fail_count;
      acc.incomplete += b.incomplete_count;
      return acc;
    },
    { rst: 0, net: 0, samples: 0, pass: 0, fail: 0, incomplete: 0 },
  );

  const barData = buckets.map((b) => ({
    date: b.bucket_start,
    RSTs: b.rst_count,
    Net_t: Number((b.net_weight_kg_total / 1000).toFixed(1)),
  }));

  const pieData = [
    { name: "Pass", value: totals.pass, color: PIE_COLORS[0] },
    { name: "Fail", value: totals.fail, color: PIE_COLORS[1] },
    { name: "Incomplete", value: totals.incomplete, color: PIE_COLORS[2] },
  ].filter((s) => s.value > 0);

  return (
    <>
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <TotalCard label="RSTs" value={format.int(totals.rst)} />
        <TotalCard label="Net (tonnes)" value={format.tonnes(totals.net)} />
        <TotalCard label="Samples" value={format.int(totals.samples)} />
        <TotalCard
          label="Pass rate"
          value={
            totals.samples > 0
              ? `${((totals.pass / totals.samples) * 100).toFixed(0)}%`
              : "—"
          }
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="bg-white rounded shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            RSTs + tonnage per bucket
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="RSTs" fill="#2e7d32" />
              <Bar dataKey="Net_t" fill="#f9a825" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Sample verdict split
          </h3>
          {pieData.length === 0 ? (
            <div className="text-sm text-gray-500 italic py-16 text-center">
              No samples in this window.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>
    </>
  );
}

function TotalCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded shadow p-3">
      <div className="text-xs text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="text-2xl font-semibold text-agrilens-green-dark">
        {value}
      </div>
    </div>
  );
}
